import React from "react";
import {
  Navbar,
  Collapse,
  Typography,
  Button,
  IconButton,
} from "@material-tailwind/react";
import UserMenu from "./UserMenu";
import SearchBar from "../SearchBar";
import { Link, NavLink, matchPath } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";
import { useLocation } from "react-router-dom";

const Header = () => {
  const [openNav, setOpenNav] = React.useState(false);
  const { user, isAuth } = useAuth();
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 960 : false,
  );
  const [scrolled, setScrolled] = React.useState(false);

  const location = useLocation();
  const currentPath = location.pathname;
  const isCourseSearchPage = currentPath === "/courses";
  const navLinkClass = (isActive) =>
    `flex items-center gap-x-1 p-1 font-semibold text-sm xl:text-base whitespace-nowrap rounded-md border transition ${
      isActive
        ? "border-transparent shadow-md shadow-blue-gray-500 bg-blue-50/70"
        : "border-transparent hover:rounded-md hover:shadow-md hover:shadow-blue-gray-500 hover:bg-blue-50/70"
    }`;
  const isRouteActive = (patterns) =>
    patterns.some((pattern) =>
      Boolean(matchPath({ path: pattern, end: pattern === "/" }, currentPath)),
    );

  // Handle Navbar Scroll Effect
  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50); // Trigger at 50px scroll
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  React.useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth < 960;
      setIsMobile(nextIsMobile);
      if (!nextIsMobile) {
        setOpenNav(false); // Close mobile nav when switching to desktop
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    setOpenNav(false); // Close the mobile menu on every route change
  }, [location.pathname]);

  const navList = (
    <ul className="mt-2 mb-4 flex flex-col gap-2 lg:mb-0 lg:mt-0 lg:flex-row lg:items-center lg:gap-3 xl:gap-4 lg:flex-nowrap">
      {/* Home menu temporarily hidden */}
      <Typography
        as="li"
        variant="small"
        color="blue-gray"
        className="flex items-center gap-x-2 p-1 font-medium"
      >
        <NavLink
          to="/courses"
          className={() =>
            navLinkClass(isRouteActive(["/courses/*", "/category/*"]))
          }
        >
          {t("courses")}
        </NavLink>
      </Typography>
      <Typography
        as="li"
        variant="small"
        color="blue-gray"
        className="flex items-center gap-x-2 p-1 font-medium"
      >
        <NavLink
          to="/certificates/verify"
          className={() =>
            navLinkClass(isRouteActive(["/certificates/verify"]))
          }
        >
          {t("certificates")}
        </NavLink>
      </Typography>
      <Typography
        as="li"
        variant="small"
        color="blue-gray"
        className="flex items-center gap-x-2 p-1 font-medium"
      >
        <NavLink
          to="/attachments"
          className={() =>
            navLinkClass(isRouteActive(["/attachments/*", "/guidance/*"]))
          }
        >
          {t("nav.attachments")}
        </NavLink>
      </Typography>
      <Typography
        as="li"
        variant="small"
        color="blue-gray"
        className="flex items-center gap-x-2 p-1 font-medium"
      >
        <NavLink
          to="/about"
          className={() => navLinkClass(isRouteActive(["/about"]))}
        >
          {t("about-us")}
        </NavLink>
      </Typography>
      <Typography
        as="li"
        variant="small"
        color="blue-gray"
        className="flex items-center gap-x-2 p-1 font-medium"
      >
        <NavLink
          to="/faq"
          className={() => navLinkClass(isRouteActive(["/faq"]))}
        >
          {t("faq")}
        </NavLink>
      </Typography>
      <Typography
        as="li"
        variant="small"
        color="blue-gray"
        className="flex items-center gap-x-2 p-1 font-medium"
      >
        <NavLink
          to="/contact"
          className={() => navLinkClass(isRouteActive(["/contact"]))}
        >
          {t("contact-us")}
        </NavLink>
      </Typography>
    </ul>
  );

  const isUserAuth = user?.fullName && isAuth;

  return (
    <header className="sticky top-0 z-50 text-black transition-all border-b bg-white pb-2 shadows">
      {/* <div className="w-full bg-yellow-700 text-wheat text-center py-0.5 text-sm font-semibold">
        <span className="block sm:inline">
          Beta version / نشرات آزمایشی / امتحاني نشرات
        </span>
      </div> */}

      <Navbar className="mx-auto h-max max-w-screen-2xl relative z-[60] px-2 py-1.5 lg:px-4 lg:py-2 border-none shadow-none rounded-none bg-transparent backdrop-blur-none">
        {/* <Navbar className="mx-auto h-max max-w-screen-2xl sticky top-0 z-50 lg:-4 px-2 py- lg:px-4 border-none shadow-none rounded-none bg-none backdrop-blur-none backdrop-saturate- bg-opacity-0"> */}
        <div className="mx-auto flex flex-wrap items-center justify-between mt-1 text-black">
          <Link
            to="/"
            className={`flex items-center gap-2 transition-all duration-300 ${
              scrolled ? "mt-1.5" : ""
            }`}
          >
            <picture>
              {/* Show this image on small screens */}
              <source media="(max-width: 640px)" srcSet="logo512.png" />

              {/* Default logo for medium and larger screens */}
              <img
                src={t("header_logo")}
                alt="logo"
                className="h-10 sm:h-12 w-auto object-contain rounded-md"
                loading="lazy"
              />
            </picture>
          </Link>

          <div className="absolute ltr:right-[5.5rem] rtl:left-10 flex justify-center items-center sm:hidden">
            {/* <!-- Mobile --> */}
            {isUserAuth && <UserMenu />}
          </div>

          <div className="absolute ltr:right-10 rtl:left-[5.5rem] flex justify-center items-center sm:hidden">
            {/* <!-- Mobile --> */}
            <LanguageSelector />
          </div>

          <div className="hidden lg:block">{navList}</div>

          <div className="flex justify-between items-center">
            {!isCourseSearchPage && <div className="ml-auto px-3">
              <SearchBar isMobile={isMobile} />
            </div>}

            <div className="absolute inset-y-0 hidden right-0 sm:flex items-center sm:static sm:inset-auto sm:mx-3 sm:pr-0">
              <LanguageSelector />
            </div>

            <div className="hidden lg:block">
              <div className="flex justify-center items-center gap-x-1 ">
                {isUserAuth ? (
                  <div className="flex justify-between items-center gap-x-1 ">
                    <UserMenu />
                  </div>
                ) : (
                  <Link to="/login">
                    <Button
                      fullWidth
                      variant="text"
                      color="#000"
                      size="sm"
                      className="hover:border-gray-700 hover:bg-gray-700 hover:text-white transition duration-200 ease-in-out text-black border border-black"
                    >
                      <span className="">{t("sign_in")}</span>
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
          <IconButton
            variant="text"
            className="ltr:ml-auto rtl:mr-auto h-6 w-6 text-inherit hover:bg-transparent focus:bg-transparent active:bg-transparent lg:hidden"
            ripple={false}
            onClick={() => setOpenNav(!openNav)}
          >
            {openNav ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                className="h-6 w-6"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </IconButton>
        </div>
        <Collapse open={openNav} className="bg-teal-50">
          <div className="container mx-auto">
            {navList}
            {/* <SearchBar isMobile /> */}
            <div className="flex justify-center items-center gap-x-1 mx-3">
              {isUserAuth ? (
                <Link to="/login" className="w-full my-3">
                  <Button
                    fullWidth
                    variant="text"
                    size="sm"
                    className="hover:border-gray-700 hover:bg-gray-700 hover:text-white transition duration-200 ease-in-out text-black border border-black"
                  >
                    <span className="">{t("logout")}</span>
                  </Button>
                </Link>
              ) : (
                <Link to="/login" className="w-full my-3">
                  <Button
                    fullWidth
                    variant="text"
                    color="black"
                    size="sm"
                    className="hover:border-gray-700 hover:bg-gray-700 hover:text-white transition duration-200 ease-in-out text-black border border-black"
                  >
                    <span className="">{t("login")}</span>
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </Collapse>
      </Navbar>
    </header>
  );
};

export default Header;
